import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import {
  IAdministrativeGeographyGeometry,
  IStandardGeographyDto,
} from "features/MarketAreas/types";
import { resetMarketAreaDrawing } from "../resetMarketAreaDrawing";
import { TAdministrativeGeographyStage } from "../types";
import { buildGeographyCacheKey } from "./buildGeographyCacheKey";
import {
  loadChildGeographies,
  loadRootGeographies,
  resolveGeographyAtPoint,
} from "./controllers";

type TMarketAreaAdministrativeGeographyState = {
  drawnGeometry: IAdministrativeGeographyGeometry | null;
  editedGeometry: IAdministrativeGeographyGeometry | null;
  boundaries: IStandardGeographyDto[];
  stage: TAdministrativeGeographyStage;
  restartToken: number;
  isLoading: boolean;
  currentRequestId: string | null;
  geographyCache: Record<string, IStandardGeographyDto[]>;
};

const initialState: TMarketAreaAdministrativeGeographyState = {
  drawnGeometry: null,
  editedGeometry: null,
  boundaries: [],
  stage: "states",
  restartToken: 0,
  isLoading: false,
  currentRequestId: null,
  geographyCache: {},
};

type TBoundariesLoadedPayload = {
  boundaries: IStandardGeographyDto[];
  stage: TAdministrativeGeographyStage;
};

const marketAreaAdministrativeGeographySlice = createSlice({
  name: "marketAreaAdministrativeGeography",
  initialState,
  reducers: {
    setDrawnAdministrativeGeography: (
      state,
      { payload }: PayloadAction<IAdministrativeGeographyGeometry | null>
    ) => {
      state.drawnGeometry = payload;
    },
    setAdministrativeEditedGeometry: (
      state,
      { payload }: PayloadAction<IAdministrativeGeographyGeometry | null>
    ) => {
      state.editedGeometry = payload;
    },
    administrativeBoundariesLoaded: (
      state,
      { payload }: PayloadAction<TBoundariesLoadedPayload>
    ) => {
      state.boundaries = payload.boundaries;
      state.stage = payload.stage;
    },
    setAdministrativeBoundaries: (
      state,
      { payload }: PayloadAction<IStandardGeographyDto[]>
    ) => {
      state.boundaries = payload;
    },
    setAdministrativeStage: (
      state,
      { payload }: PayloadAction<TAdministrativeGeographyStage>
    ) => {
      state.stage = payload;
    },
    restartAdministrativeGeography: (state) => {
      state.restartToken += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetMarketAreaDrawing, (state) => ({
        ...initialState,
        geographyCache: state.geographyCache,
      }))
      .addCase(loadRootGeographies.fulfilled, (state, { payload, meta }) => {
        const key = buildGeographyCacheKey({ type: meta.arg });
        state.geographyCache[key] = payload;
      })
      .addCase(loadChildGeographies.fulfilled, (state, { payload, meta }) => {
        const key = buildGeographyCacheKey({
          type: meta.arg.childType,
          parentLayer: meta.arg.parent.layer,
          parentId: meta.arg.parent.id,
        });
        state.geographyCache[key] = payload;
      })
      .addMatcher(
        isAnyOf(
          loadRootGeographies.pending,
          loadChildGeographies.pending,
          resolveGeographyAtPoint.pending
        ),
        (state, { meta }) => {
          state.currentRequestId = meta.requestId;
          state.isLoading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          loadRootGeographies.fulfilled,
          loadChildGeographies.fulfilled,
          loadRootGeographies.rejected,
          loadChildGeographies.rejected,
          resolveGeographyAtPoint.fulfilled,
          resolveGeographyAtPoint.rejected
        ),
        (state, { meta }) => {
          if (state.currentRequestId !== meta.requestId) return;
          state.currentRequestId = null;
          state.isLoading = false;
        }
      );
  },
});

export const {
  setDrawnAdministrativeGeography,
  setAdministrativeEditedGeometry,
  administrativeBoundariesLoaded,
  setAdministrativeBoundaries,
  setAdministrativeStage,
  restartAdministrativeGeography,
} = marketAreaAdministrativeGeographySlice.actions;

export default marketAreaAdministrativeGeographySlice;
